export interface Agent {
  id: string;
  code: string;
  name: string;
  tagline: string | null;
  role_description: string | null;
  personality: string | null;
  communication_style: string | null;
  model_provider: string;
  model_id: string;
  is_active: boolean;
  goals: string[] | null;
  skills: string[] | null;
  model_config_json: Record<string, unknown> | null;
  comms_channels: string[] | null;
  heartbeat_cron: string | null;
  heartbeat_tasks: string[] | null;
  reports_to: string | null;
}

export interface AgentListResponse {
  agents: Agent[];
  total: number;
}

export type KBCategory =
  | "purpose_mission_vision_values"
  | "policy_process"
  | "ideas_notes_snippets"
  | "lessons"
  | "brand_style"
  | "resources"
  | "product_definition"
  | "service_definition"
  | "meeting_notes"
  | "system_documentation"
  | "feature_request"
  | "bug_report";

export type KBStatus = "draft" | "published" | "archived";

export type WebsiteCategory =
  | "news"
  | "service"
  | "case_study"
  | "team_member"
  | "page"
  | "blog_post"
  | "linkedin_post";

export const WEBSITE_CATEGORY_LABELS: Record<WebsiteCategory, string> = {
  news: "News",
  service: "Service",
  case_study: "Case Study",
  team_member: "Team Member",
  page: "Page",
  blog_post: "Blog Post",
  linkedin_post: "LinkedIn Post",
};

export const KB_CATEGORY_LABELS: Record<KBCategory, string> = {
  purpose_mission_vision_values: "Purpose / Mission / Vision / Values",
  policy_process: "Policy & Process",
  ideas_notes_snippets: "Ideas & Notes",
  lessons: "Lessons",
  brand_style: "Brand & Style",
  resources: "Resources",
  product_definition: "Product Definition",
  service_definition: "Service Definition",
  meeting_notes: "Meeting Notes",
  system_documentation: "System Documentation",
  feature_request: "Feature Request",
  bug_report: "Bug Report",
};

export interface KBArticle {
  id: string;
  title: string;
  content_markdown: string;
  content_html: string;
  category: KBCategory;
  status: KBStatus;
  tags: string[] | null;
  version: number;
  created_by: string | null;
  updated_by: string | null;
  pmvv_alignment_score: number | null;
  website_published: boolean;
  website_slug: string | null;
  website_category: WebsiteCategory | null;
  website_excerpt: string | null;
  website_hero_image_url: string | null;
  website_published_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface KBArticleListItem {
  id: string;
  title: string;
  category: KBCategory;
  status: KBStatus;
  tags: string[] | null;
  version: number;
  website_published: boolean;
  website_slug: string | null;
  updated_at: string;
}

export interface KBArticleListResponse {
  articles: KBArticleListItem[];
  total: number;
}

// --- CRM ---

export type PipelineStage =
  | "lead"
  | "qualified"
  | "proposal"
  | "negotiation"
  | "won"
  | "lost"
  | "dormant";
export type SizeBand = "sole_trader" | "small" | "medium" | "large" | "enterprise";
export type EngagementType = "one_off" | "retainer";

export const PIPELINE_LABELS: Record<PipelineStage, string> = {
  lead: "Lead",
  qualified: "Qualified",
  proposal: "Proposal",
  negotiation: "Negotiation",
  won: "Won",
  lost: "Lost",
  dormant: "Dormant",
};

export const PIPELINE_COLORS: Record<PipelineStage, string> = {
  lead: "#90CAF9",
  qualified: "#CE93D8",
  proposal: "#FFB74D",
  negotiation: "#FFF176",
  won: "#A5D6A7",
  lost: "#EF9A9A",
  dormant: "#B0BEC5",
};

export interface CRMCompany {
  id: string;
  name: string;
  industry: string | null;
  size_band: SizeBand | null;
  website: string | null;
  address_line1: string | null;
  address_line2: string | null;
  city: string | null;
  postcode: string | null;
  country: string | null;
  phone: string | null;
  email_general: string | null;
  source: string | null;
  pipeline_stage: PipelineStage;
  freeagent_id: string | null;
  notes: string | null;
  tags: string[] | null;
  assigned_to: string | null;
  created_at: string;
  updated_at: string;
}

export interface CRMOpportunity {
  id: string;
  company_id: string;
  title: string;
  pipeline_stage: PipelineStage;
  status: "open" | "won" | "lost";
  quote_value: number | null;
  total_hours_required: number | null;
  other_costs: number | null;
  engagement_type: EngagementType | null;
  likely_start_date: string | null;
  // For retainer engagements: quote_value is interpreted as the monthly
  // amount; the displayed contract value = quote_value × months between
  // these dates (inclusive). For one-offs these are informational.
  contract_start_date: string | null;
  contract_end_date: string | null;
  notes: string | null;
  assigned_to: string | null;
  closed_at: string | null;
  created_at: string;
  updated_at: string;
}

// CRM Communication type (added so the edit + delete UI can be typed)
export type CommType = "email" | "call" | "meeting" | "note" | "linkedin" | "other";
export type CommDirection = "inbound" | "outbound";

export interface CRMCommunication {
  id: string;
  company_id: string | null;
  individual_id: string | null;
  type: CommType;
  direction: CommDirection;
  subject: string | null;
  content: string | null;
  logged_by: string | null;
  occurred_at: string;
  created_at: string;
}

export type CampaignStatus = "planned" | "active" | "completed" | "cancelled";

export const CAMPAIGN_STATUS_LABELS: Record<CampaignStatus, string> = {
  planned: "Planned",
  active: "Active",
  completed: "Completed",
  cancelled: "Cancelled",
};

export const CAMPAIGN_STATUS_COLORS: Record<
  CampaignStatus,
  "default" | "primary" | "success" | "warning"
> = {
  planned: "default",
  active: "primary",
  completed: "success",
  cancelled: "warning",
};

export interface Campaign {
  id: string;
  name: string;
  description: string | null;
  status: CampaignStatus;
  goal: string | null;
  target_segment: string | null;
  owner_agent: string | null;
  start_date: string | null;
  end_date: string | null;
  tags: string[] | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface CampaignStats {
  communication_count: number;
  followup_count: number;
  pending_followup_count: number;
}

export interface CRMIndividual {
  id: string;
  company_id: string | null;
  first_name: string;
  last_name: string;
  job_title: string | null;
  email: string | null;
  phone: string | null;
  mobile: string | null;
  linkedin_url: string | null;
  is_primary_contact: boolean;
  source: string | null;
  tags: string[] | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface HealthResponse {
  status: string;
  environment: string;
  version: string;
}

export interface PmvvValue {
  title: string;
  body: string;
}

export interface PmvvSettings {
  purpose: string;
  mission: string;
  vision: string;
  values: PmvvValue[];
  updated_at: string;
}

export interface WorkspaceStatus {
  connected: boolean;
  provider: string | null;
  granted_scopes: string[];
  connected_at: string | null;
  last_refreshed_at: string | null;
}

export interface OAuthStartResponse {
  authorize_url: string;
}

// ---------- Personal-agent chat (Phase 2F) ----------

export type ToolInvocationStatus =
  | "pending"
  | "rejected"
  | "executing"
  | "succeeded"
  | "failed"
  | "scope_missing";

export type PersonalAgentMessageRole = "user" | "assistant" | "tool";

export interface ConversationSummary {
  id: string;
  title: string | null;
  created_at: string;
  last_message_at: string;
}

export interface ConversationMessage {
  id: string;
  role: PersonalAgentMessageRole;
  content: string;
  tool_name: string | null;
  tool_arguments: Record<string, unknown> | null;
  tool_invocation_id: string | null;
  tool_invocation_status: ToolInvocationStatus | null;
  tool_result: Record<string, unknown> | null;
  created_at: string;
}

export interface ConversationDetail {
  conversation: ConversationSummary;
  messages: ConversationMessage[];
}

export interface SendMessageResponse {
  conversation: ConversationSummary;
  new_messages: ConversationMessage[];
  halted_on_pending: boolean;
}

export interface ToolInvocation {
  id: string;
  tool_name: string;
  requires_confirmation: boolean;
  status: ToolInvocationStatus;
  params_redacted: Record<string, unknown>;
  result_size_bytes: number | null;
  error_message_redacted: string | null;
  missing_scopes: string[] | null;
  created_at: string;
  confirmed_at: string | null;
  completed_at: string | null;
}

// ---------- Personal-agent schedules ----------

export type ScheduleFireStatus = "succeeded" | "failed" | "skipped_concurrent" | "skipped_inactive";

export interface Schedule {
  id: string;
  owner_user_id: string | null;
  agent_id: string | null;
  name: string;
  cron_expression: string;
  timezone: string;
  prompt: string;
  is_active: boolean;
  is_system: boolean;
  next_fire_at: string | null;
  last_fired_at: string | null;
  last_conversation_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface ScheduleCreate {
  agent_id?: string | null;
  name: string;
  cron_expression: string;
  prompt: string;
  timezone?: string;
  is_active?: boolean;
}

export interface ScheduleUpdate {
  name?: string;
  cron_expression?: string;
  prompt?: string;
  timezone?: string;
  is_active?: boolean;
}

export interface ScheduleFire {
  id: string;
  schedule_id: string;
  fired_at: string;
  completed_at: string | null;
  status: ScheduleFireStatus;
  conversation_id: string | null;
  error_message: string | null;
  output_summary: string | null;
}

// ---------- KB Ingest (Phase 3) ----------

export type KBAttachmentProcessingStatus = "pending" | "processing" | "done" | "failed";

export interface UploadUrlResponse {
  attachment_id: string;
  upload_url: string;
  object_path: string;
  ttl_seconds: number;
}

export interface IngestAttachment {
  id: string;
  file_name: string;
  mime_type: string;
  file_size_bytes: number;
  processing_status: KBAttachmentProcessingStatus;
  derived_article_id: string | null;
  processing_error: string | null;
  created_at: string;
  processing_started_at: string | null;
  processing_completed_at: string | null;
}
